import Link from 'next/link';
import { type FC, type MutableRefObject } from 'react';
import { KaimonoWebRoutes } from 'src/config/routes';
import type { BrandsMap } from 'src/types/brand';
import { extractDataByIndex } from '.';
import { BrandList } from './BrandList';
import { ImageList } from './ImageList';
import { ToggleButton } from './ToggleButton';

type Props = {
  handleToggleClick: () => void;
  handleClickBrandList: (brandIndex: string) => void;
  initialVisibleCount: number;
  initialEndRef: MutableRefObject<HTMLDivElement | null>;
  listEndRef: MutableRefObject<HTMLDivElement | null>;
  Heading: 'h2' | 'h3';
  activeIndex: string | null;
  visibleCount: number;
  isSeeMoreButtonExpanded: boolean;
  headingTitle: string;
  data: BrandsMap[];
  footerImageList: {
    path: string;
    width: number;
    height: number;
  }[];
};

export const Presenter: FC<Props> = ({
  handleToggleClick,
  handleClickBrandList,
  initialEndRef,
  initialVisibleCount,
  listEndRef,
  Heading,
  activeIndex,
  visibleCount,
  isSeeMoreButtonExpanded,
  headingTitle,
  data,
  footerImageList
}) => {
  const renderIndexedBrandList = () => (
    <ul
      className="w-full grid gap-3 place-items-center"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(1.75rem, 1fr))' }}
    >
      {data.map((indexedBrands) => {
        const brandIndex = Object.keys(indexedBrands)[0];
        return (
          <BrandList
            key={brandIndex}
            brands={indexedBrands}
            isActive={activeIndex === brandIndex}
            onClick={() => handleClickBrandList(brandIndex)}
          />
        );
      })}
    </ul>
  );

  const renderActiveIndexedContent = () => {
    if (!activeIndex) return null;
    const extractedData: BrandsMap = extractDataByIndex(data, activeIndex);
    const isToggleButtonVisible = initialVisibleCount < extractedData[activeIndex].length;
    return (
      <div className="w-full flex flex-col">
        <p className="py-2 px-5 text-sm font-semibold text-darkSlate border-b border-gray-100 border-solid">
          {activeIndex}
        </p>
        <ul className="py-4 px-5 grid grid-cols-2 sm:grid-cols-3 semiLg:grid-cols-4 gap-5">
          {activeIndex &&
            extractedData[activeIndex].slice(0, visibleCount).map((brand) => (
              <li key={brand.brandId} className="text-xs px-2">
                <Link href={KaimonoWebRoutes.brands.show(brand.brandId)}>
                  <a className="text-darkSlate">{brand.name}</a>
                </Link>
              </li>
            ))}
        </ul>
        <div ref={listEndRef} />
        {isToggleButtonVisible && (
          <div className="flex justify-center py-4">
            <ToggleButton onClick={handleToggleClick} isExpanded={isSeeMoreButtonExpanded} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div ref={initialEndRef} />
      <Heading className="px-5 py-2 border-b border-black border-solid">{headingTitle}</Heading>
      <div className="flex justify-center py-5 px-4">{renderIndexedBrandList()}</div>
      {activeIndex && renderActiveIndexedContent()}
      <div className="flex justify-center semiLg:hidden">
        <ImageList footerImageList={footerImageList} />
      </div>
    </div>
  );
};
